import { useMemo, useState } from "react";
import { Button, Col, Container, Dropdown, Row } from "react-bootstrap";
import TableLayout from "components/TableLayout";
import { CommonSelect, CstmPagination } from "components/Common/Common";
import { useNavigate } from "react-router-dom";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import LoadingOverlay from "components/shared/LoadingOverlay";
import TableHeader from "components/shared/FormComponents/TableHeader";
import { dateFormat, formatString } from "helpers/common";
import { useDispatch, useSelector } from "react-redux";
import { auditLogsFilter, initialState } from "store/slices/filter";
import { useAuditLogsEventTypeListQuery, useAuditLogsListQuery } from "store/services/utility";
import ViewMetaDataPopup from "./Components/ViewMetaDataPopup";
import useModal from "hooks/useModal";
import { isEmpty } from "lodash";
import CustomDateRangePicker from "components/shared/DateRangePicker";
import AdvancedFilterPopup from "./Components/AdvancedFiltersPopup";

const AuditLogs = () => {
  const navigate = useNavigate();
  
  const dispatch = useDispatch();
  const filter = useSelector(state => state?.filter?.auditLogsFilter);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { show: showMetadataPopup, data: popupData, open: openMetadataPopup, close: closeMetadataPopup } = useModal();

  const { data, isLoading, isError, isFetching } = useAuditLogsListQuery(
    { page: currentPage, limit: pageSize, ...filter },
    { refetchOnFocus: true }
  );

  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const logsList = data?.records;

  const { data: eventTypesList, isLoading: eventTypesLoading } = useAuditLogsEventTypeListQuery();

  const eventTypeOptions = useMemo(() => {
    if(!eventTypesList?.length) {
      return [];
    }

    return eventTypesList.map(event => ({
      value: event.event_type,
      label: formatString(event.event_type)
    }))
  }, [eventTypesList])

  const resetFilter = () => {
    dispatch(auditLogsFilter(initialState.auditLogsFilter));
    setCurrentPage(1);
  };

  const column = useMemo(() => ([
    // {
    //   head: "#",
    //   component: (item, index) => (
    //     <div className="d-flex align-items-center gap-10">
    //       <h6 className="m-0 fw-sbold">{index + 1}</h6>
    //     </div>
    //   ),
    // },
    {
      head: "Log ID",
      sorter: true,
      component: (item, ind) => (
        <div className="d-flex align-items-center gap-10">
          <>{item?.id || "--"}</>
        </div>
      ),
    },
    {
      head: "Event Type",
      sorter: true,
      component: (item, ind) => (
        <div className="d-flex align-items-center gap-10">
          <>{formatString(item?.event_type)}</>
        </div>
      ),
    },
    {
      head: "Panel",
      accessor: "panel",
      sorter: true,
    },
    {
      head: "User",
      component: (item, ind) => (
        !isEmpty(item?.metadata?.user) ? (
          <div className="">
            <p className="m-0 p-0">
              <strong>Name: </strong> {item?.metadata?.user?.username}
            </p>
            <p className="m-0 p-0">
              <strong>Email: </strong> {item?.metadata?.user?.email}
            </p>
          </div>
        ):(
          <p className="m-0 p-0">
            <strong>User ID:</strong> {item?.user_id}
          </p>
        )
      ),
    },
    {
      head: "Event Date & Time",
      accessor: "action_at",
      sorter: true,
      isComponent: true,
      component: (item, ind) => (
        <p>{dateFormat(item?.action_at, "MMM D, YYYY hh:mm A")}</p>
      ),
    },
    {
      head: "Request Body",
      accessor: "",
      isComponent: true,
      component: (item) => (
        !isEmpty(item?.request_body) ? (
        <Button
          className="btn text-white d-flex align-items-center justify-content-center commonBtn fw-sbold"
          onClick={(e) => {
            e.stopPropagation();
            openMetadataPopup(item?.request_body)
          }}
        >
          View
        </Button>
        ):(
          <p>N/A</p>
        )
      ),
    },
    {
      head: "Metadata",
      accessor: "",
      isComponent: true,
      component: (item) => (
        !isEmpty(item?.metadata) ? (
        <Button
          className="btn text-white d-flex align-items-center justify-content-center commonBtn fw-sbold"
          onClick={(e) => {
            e.stopPropagation();
            openMetadataPopup(item?.metadata)
          }}
        >
          View
        </Button>
        ):(
          <p>N/A</p>
        )
      ),
    },
  ]), []);

  const handleFilterChange = (key) => (value) => {
    dispatch(auditLogsFilter({ ...filter, [key]: value }))
    setCurrentPage(1); // Reset to first page when filter changes
  }

  const handlePageChange = (page) => {
    window.scrollTo(0, 0);
    setCurrentPage(page);
  }

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setCurrentPage(1); // Reset to first page when page size changes
  }

  return (
    <>
      <ViewMetaDataPopup
        showPopup={showMetadataPopup}
        closePopup={closeMetadataPopup}
        data={popupData}
      />
      <section className="School position-relative py-4">
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <TableHeader
                heading={"Audit Logs"}
                handleFilterChange={handleFilterChange}
                resetFilter={resetFilter}
                filter={filter}
                searchPlaceholder="Search..."
                hideStatusFilter={true}
                filterComponent={
                  <>
                    <li className="">
                      <Dropdown
                        onSelect={handleFilterChange("panel")}
                      >
                        <Dropdown.Toggle
                          variant="transparent"
                          className="border-0 d-flex align-items-center  justify-content-center"
                          id="dropdown-basic"
                        >
                          {filter.panel || "Select Panel" }
                          <span className="icn ms-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="8"
                              viewBox="0 0 12 8"
                              fill="none"
                            >
                              <path
                                d="M1.415 0.298828L6 4.731L10.585 0.298828L12 1.66666L6 7.46666L0 1.66666L1.415 0.298828Z"
                                fill="black"
                              />
                            </svg>
                          </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          style={{
                            maxHeight: "200px",
                            overflowY: "scroll"
                          }}
                        >
                          <Dropdown.Item eventKey={"Admin"}>Admin</Dropdown.Item>
                          <Dropdown.Item eventKey={"Therapist"}>Therapist</Dropdown.Item>
                          <Dropdown.Item eventKey={"Sso"}>SSO</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </li>
                    <li className="">
                      <CommonSelect
                        isFilter={true}
                        defaultValue={eventTypeOptions?.find(option => option.value === filter.event_type)}
                        option={eventTypeOptions}
                        placeholder={"Event Type"}
                        onChange={(selected) => {
                          handleFilterChange("event_type")(selected?.value);
                        }}
                      />
                    </li>
                    <li>
                      <CustomDateRangePicker
                        value={[filter.start_date, filter.end_date]}
                        onChange={(dateRange) => {
                          if((dateRange[0] && dateRange[1]) || (!dateRange[0] && !dateRange[1])) {
                            dispatch(auditLogsFilter({
                              ...filter,
                              start_date: dateRange[0] || "",
                              end_date: dateRange[1] || "",
                            }))
                          }
                        }}
                      />
                    </li>
                  </>
                }
              >
                <AdvancedFilterPopup />
              </TableHeader>
              <div className="table-outer">
                {isLoading ? (
                  <div style={{ height: "200px" }}>
                    <LoadingIndicator />
                  </div>
                ) : isError ? (
                  <ErrorComponent />
                ) : (
                  <div>
                    {isFetching && (
                      <LoadingOverlay />
                    )}
                    <TableLayout data={logsList} column={column} filter={filter} handleSort={handleFilterChange("sort")} />
                  </div>
                )}
                <CstmPagination
                  totalPages={totalPages}
                  activePage={currentPage}
                  handlePageChange={handlePageChange}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AuditLogs;
